import { useState, useEffect, useCallback } from 'react';

interface UserInfo {
  name?: string;
  email?: string;
  id?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userInfo: UserInfo | null;
  accessToken: string | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    error: null,
    userInfo: null,
    accessToken: null,
  });

  const authenticateUser = useCallback(async () => {
    // Access the global pca variable from Taskpane.tsx
    const globalPca = (window as any).pca;
    if (!globalPca) {
      setAuthState(prev => ({ ...prev, error: 'MSAL not initialized', isLoading: false }));
      return;
    }

    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Specify scopes needed for the access token
      // Contacts.Read.Shared provides access to both shared mailbox contacts and personal contacts
      const tokenRequest = {
        scopes: [
          'User.Read', 
          'openid', 
          'profile', 
          'email',
          'Contacts.Read.Shared',
          'User.Read.All',
        ],
      };

      let accessToken: string | null = null;

      // Try to acquire token silently first
      try {
        console.log('Trying to acquire token silently...');
        const userAccount = await globalPca.acquireTokenSilent(tokenRequest);
        console.log('Acquired token silently.');
        accessToken = userAccount.accessToken;
        setAuthState(prev => ({
          ...prev,
          userInfo: {
            name: userAccount.account?.name,
            email: userAccount.account?.username,
            id: userAccount.account?.localAccountId,
          },
          isAuthenticated: true,
          accessToken,
        }));
      } catch (silentError) {
        console.log(`Unable to acquire token silently: ${silentError}`);
      }

      // If silent acquisition failed, try interactive
      if (accessToken === null) {
        try {
          console.log('Trying to acquire token interactively...');
          const userAccount = await globalPca.acquireTokenPopup(tokenRequest);
          console.log('Acquired token interactively.');
          accessToken = userAccount.accessToken;
          setAuthState(prev => ({
            ...prev,
            userInfo: {
              name: userAccount.account?.name,
              email: userAccount.account?.username,
              id: userAccount.account?.localAccountId,
            },
            isAuthenticated: true,
            accessToken,
          }));
        } catch (interactiveError) {
          console.error('Failed to acquire token interactively:', interactiveError);
          setAuthState(prev => ({ 
            ...prev, 
            error: 'Authentication failed. Please try again.',
            isLoading: false 
          }));
          return;
        }
      }

      // If we have a token, get user info from Microsoft Graph
      if (accessToken) {
        await fetchUserInfoFromGraph(accessToken);
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setAuthState(prev => ({ 
        ...prev, 
        error: 'Authentication failed. Please try again.',
        isLoading: false 
      }));
    } finally {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const fetchUserInfoFromGraph = async (accessToken: string) => {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setAuthState(prev => ({
          ...prev,
          userInfo: {
            name: userData.displayName,
            email: userData.mail || userData.userPrincipalName,
            id: userData.id,
          },
        }));
      } else {
        console.error('Failed to fetch user info from Graph API');
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
    }
  };

  const signOut = async () => {
    const globalPca = (window as any).pca;
    if (globalPca) {
      try {
        await globalPca.logoutPopup();
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          error: null,
          userInfo: null,
          accessToken: null,
        });
      } catch (err) {
        console.error('Sign out error:', err);
        setAuthState(prev => ({ ...prev, error: 'Sign out failed' }));
      }
    }
  };

  const getTokenForContacts = async () => {
    const globalPca = (window as any).pca;
    if (!globalPca) {
      throw new Error('MSAL not initialized');
    }

    const tokenRequest = {
      scopes: [
        'User.Read', 
        'openid', 
        'profile', 
        'email',
        'Contacts.Read.Shared',
      ],
    };

    const userAccount = await globalPca.acquireTokenSilent(tokenRequest);
    return userAccount.accessToken;
  };

  return {
    ...authState,
    authenticateUser,
    signOut,
    getTokenForContacts,
  };
};

